import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Chip } from '@mui/material';
import BoardTemplateManager from '../boardTemplateManager';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ProfileButton, 
  Avatar, 
  StyledMenu, 
  MenuHeader, 
  UserName, 
  UserEmail, 
  StyledMenuItem, 
  MenuItemText, 
  StyledDivider,
  LargeAvatar
} from './styles';

const ProfileMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const open = Boolean(anchorEl);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };
  
  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleAdminUsers = () => {
    navigate('/admin/users');
    handleClose();
  };

  const handleTemplateManager = () => {
    setTemplateManagerOpen(true);
    handleClose();
  };

  const getInitials = (name: string | undefined): string => {
    if (!name) return '?';
    
    return name
      .split(' ')
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Função para obter o nome completo do usuário
  const getFullName = (user: any): string => {
    if (!user) return '';
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.username || '';
  };

  return (
    <>
      <ProfileButton
        onClick={handleClick}
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {user?.avatarUrl ? (
          <Avatar src={user.avatarUrl} alt={getFullName(user)} />
        ) : (
          <Avatar>{getInitials(getFullName(user))}</Avatar>
        )}
      </ProfileButton>
      
      <StyledMenu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'profile-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuHeader>
          {user?.avatarUrl ? (
            <LargeAvatar src={user.avatarUrl} alt={getFullName(user)} />
          ) : (
            <LargeAvatar>{getInitials(getFullName(user))}</LargeAvatar>
          )}
          <UserName variant="body1">{getFullName(user) || 'Usuário'}</UserName>
          <UserEmail variant="body2">{user?.email || 'usuario@exemplo.com'}</UserEmail>
          
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            {user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'administrator' || user?.role === 'Administrator' ? (
              <Chip
                icon={<SupervisorAccountIcon style={{ fontSize: 16 }} />}
                label="Administrador do Sistema"
                size="small"
                sx={{
                  backgroundColor: 'rgba(156, 39, 176, 0.12)',
                  color: '#9c27b0',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#9c27b0' }
                }}
              />
            ) : user?.role === 'Developer' ? (
              <Chip
                icon={<PeopleIcon style={{ fontSize: 16 }} />}
                label="Desenvolvedor"
                size="small"
                sx={{
                  backgroundColor: 'rgba(233, 30, 99, 0.12)',
                  color: '#e91e63',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#e91e63' }
                }}
              />
            ) : (
              <Chip
                icon={<PersonIcon style={{ fontSize: 16 }} />}
                label="Usuário"
                size="small"
                sx={{
                  backgroundColor: 'rgba(96, 125, 139, 0.12)',
                  color: '#607d8b',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#607d8b' }
                }}
              />
            )}
          </Box>
        </MenuHeader>
        
        {/* <StyledDivider />
        
        <StyledMenuItem onClick={handleProfile}>
          <PersonIcon fontSize="small" />
          <MenuItemText>Meu Perfil</MenuItemText>
        </StyledMenuItem>
        
        <StyledMenuItem>
          <SettingsIcon fontSize="small" />
          <MenuItemText>Configurações</MenuItemText>
        </StyledMenuItem> */}
        
        {/* Menu de administrador - exibido apenas para admins e desenvolvedores */}
        {(user?.role === 'Admin' || user?.role === 'Administrator' || user?.role === 'admin' || user?.role === 'administrator' || user?.role === 'Developer') && (
          <>
            <StyledDivider />
            <StyledMenuItem onClick={handleAdminUsers}>
              <AdminPanelSettingsIcon fontSize="small" />
              <MenuItemText>Administrar Usuários</MenuItemText>
            </StyledMenuItem>
            
            {/* Botão de Templates apenas para administradores do sistema e desenvolvedores */}
            {(user?.role?.toLowerCase() === 'admin' || 
              user?.role?.toLowerCase() === 'administrator' || 
              user?.role?.toLowerCase() === 'developer' ||
              user?.role?.toLowerCase() === 'dev' ||
              (typeof user?.role === 'string' && user?.role.toLowerCase().includes('administrador do sistema'))) && (
              <StyledMenuItem onClick={handleTemplateManager}>
                <ViewColumnIcon fontSize="small" />
                <MenuItemText>Gerenciar Templates</MenuItemText>
              </StyledMenuItem>
            )}
          </>
        )}
        
        <StyledDivider />
        
        <StyledMenuItem onClick={handleLogout}>
          <ExitToAppIcon fontSize="small" />
          <MenuItemText>Sair</MenuItemText>
        </StyledMenuItem>
      </StyledMenu>
      
      {/* Modal de Gerenciar Templates */}
      <BoardTemplateManager 
        open={templateManagerOpen}
        onClose={() => setTemplateManagerOpen(false)}
        selectMode={false}
      />
    </>
  );
};

export default ProfileMenu;